// next-app/src/components/survey/basic-survey.tsx
'use client';

import { EmailOptIn, PersonInfo, SurveyForm } from '@/components/survey';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useSubmitSurvey, useSurveyConfig } from '@/hooks';
import { basicSurveySchema, type BasicSurveyForm } from '@/lib/validations/survey';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

interface BasicSurveyProps {
  hash: string;
  eventPortal?: string;
}

export function BasicSurvey({ hash, eventPortal }: BasicSurveyProps) {
  const router = useRouter();

  // Build redirect paths with optional event_portal parameter
  const successPath = eventPortal ? `/submit/success/${eventPortal}` : '/submit/success';
  const errorPath = eventPortal ? `/submit/error/${eventPortal}` : '/submit/error';

  // Fetch survey configuration
  const { data: configData, isLoading, isError, error } = useSurveyConfig(hash);
  const config = configData?.config;

  // Form setup
  const form = useForm<BasicSurveyForm>({
    resolver: zodResolver(basicSurveySchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      event_id: 0,
      hash: hash,
      survey_type: 'basic',
      email_opt_in: false,
    },
  });

  // Update form when config loads
  useEffect(() => {
    if (config) {
      form.setValue('event_id', config.event_id);
    }
  }, [config, form]);

  // Submission Handler
  const submitSurvey = useSubmitSurvey(hash, {
    onSuccess: () => {
      router.push(successPath);
    },
    onError: () => {
      router.push(errorPath);
    },
  });

  const handleSubmit = async (data: BasicSurveyForm) => {
    submitSurvey.mutate({
      ...data,
      responses: {},
    });
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !config) {
    return (
      <div className="mx-auto max-w-2xl">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message || 'Unable to load survey. Please check the URL and try again.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <SurveyForm
      form={form}
      onSubmit={handleSubmit}
      title="Event Registration"
      description="Please complete the form below to register for this event"
      eventName={config.event_name}
      dealerName={config.dealer_name}
      isSubmitting={submitSurvey.isPending}
      submitText="Complete Registration"
    >
      <PersonInfo disabled={submitSurvey.isPending} />

      <hr className="my-6" />

      <EmailOptIn optInText={config.email_opt_in_text} disabled={submitSurvey.isPending} />
    </SurveyForm>
  );
}
